import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import { useMemo } from 'react';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type {
  ChartType,
  SelectableChartType,
} from '../PageConfigProvider/types/chart';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { ChartIcon, NonSelectableChartIcon } from './ChartIcon';

interface ChartTypeOption {
  value: ChartType;
  label: string;
  icon: React.ReactElement;
  isUserSelectable: boolean;
}

export const CHART_TYPE_OPTIONS: ChartTypeOption[] = [
  // User-selectable
  {
    value: 'bar',
    label: 'Bar',
    icon: <ChartIcon type="bar" />,
    isUserSelectable: true,
  },
  {
    value: 'map',
    label: 'Map',
    icon: <ChartIcon type="map" />,
    isUserSelectable: true,
  },
  {
    value: 'stacked-bar',
    label: 'Stacked Bar',
    icon: <ChartIcon type="stacked-bar" />,
    isUserSelectable: true,
  },
  {
    value: 'grouped-bar',
    label: 'Grouped bar',
    icon: <ChartIcon type="grouped-bar" />,
    isUserSelectable: true,
  },
  {
    value: 'diverging-bar',
    label: 'Diverging bar',
    icon: <ChartIcon type="diverging-bar" />,
    isUserSelectable: true,
  },
  {
    value: 'diverging-bar-combo',
    label: 'Diverging bar combo',
    icon: <ChartIcon type="diverging-bar-combo" />,
    isUserSelectable: true,
  },
  {
    value: 'treemap',
    label: 'Treemap',
    icon: <ChartIcon type="treemap" />,
    isUserSelectable: true,
  },
  {
    value: 'line',
    label: 'Line',
    icon: <ChartIcon type="line" />,
    isUserSelectable: true,
  },
  {
    value: 'volume',
    label: 'Volume Dynamics',
    icon: <ChartIcon type="volume" />,
    isUserSelectable: true,
  },
  {
    value: 'sankey',
    label: 'Sankey',
    icon: <ChartIcon type="sankey" />,
    isUserSelectable: true,
  },
  {
    value: 'distribution',
    label: 'Distribution',
    icon: <ChartIcon type="distribution" />,
    isUserSelectable: true,
  },
  {
    value: 'box',
    label: 'Boxplot',
    icon: <ChartIcon type="box" />,
    isUserSelectable: true,
  },
  {
    value: 'gauge',
    label: 'Gauge',
    icon: <ChartIcon type="gauge" />,
    isUserSelectable: true,
  },
  {
    value: 'table',
    label: 'Table',
    icon: <ChartIcon type="table" />,
    isUserSelectable: true,
  },
  {
    value: 'text',
    label: 'Text',
    icon: <ChartIcon type="text" />,
    isUserSelectable: true,
  },
  {
    value: 'ai-summary',
    label: 'AI Summary',
    icon: <ChartIcon type="ai-summary" />,
    isUserSelectable: true,
  },

  // Non-selectable
  {
    value: 'full-profile-info',
    label: 'Full Profile Info',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'share-of-segment',
    label: 'Share of segment',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'entity-summary',
    label: 'Summary',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'mixed-stats',
    label: 'Mixed Stats',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'sentiment_review',
    label: 'Sentiment Review',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'sentiment_effect_bar',
    label: 'Sentiment Effect',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
];

export const isUserSelectableChartType = (chartType: ChartType): boolean => {
  const option = CHART_TYPE_OPTIONS.find((opt) => opt.value === chartType);
  return option?.isUserSelectable ?? false;
};

const styles = {
  selectedBg: '#DEF5EE',
  selectedBorder: '#29B57E',
  defaultBorder: '#E2E8F0',
  textColor: '#2D3748',
  hoverBg: 'gray.50',
};

type ChartTypeSelectorProps = {
  onFirstSelect?: () => void;
  item: PositionedItem;
};

export const ChartTypeSelector = ({
  onFirstSelect,
  item,
}: ChartTypeSelectorProps) => {
  const { dispatch } = useTabContentConfig();
  const selectedChartType = item?.plot?.chartType;
  const id = item?.id;

  const handleSelect = (chartType: SingleValue<SelectableChartType>) => {
    if (!chartType) return;

    if (!selectedChartType && onFirstSelect) {
      onFirstSelect();
    }

    dispatch({ type: 'UPDATE_ITEM_CHART_TYPE', payload: { id, chartType } });
  };

  const availableOptions = CHART_TYPE_OPTIONS;

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  const initialUnselectableChart = useMemo(() => {
    const initialType = item?.plot?.chartType;
    if (initialType && !isUserSelectableChartType(initialType)) {
      return initialType;
    }
    return null;
  }, [item?.id]);

  const displayOptions = availableOptions.filter(
    (option) =>
      option.isUserSelectable ||
      option.value === selectedChartType ||
      option.value === initialUnselectableChart
  );

  return (
    <Box mb={6}>
      <VStack spacing={2} align="stretch">
        {displayOptions.map(({ value, label, icon }) => {
          const isSelected = value === selectedChartType;

          const bg = isSelected ? styles.selectedBg : 'white';
          const borderColor = isSelected
            ? styles.selectedBorder
            : styles.defaultBorder;
          const hoverBg = isSelected ? styles.selectedBg : styles.hoverBg;
          const radioBg = isSelected ? styles.selectedBorder : 'white';

          return (
            <Box
              key={value}
              display="flex"
              alignItems="center"
              px={2}
              py={1.5}
              h="40px"
              cursor="pointer"
              borderRadius="4px"
              transition="all 0.2s"
              bg={bg}
              border="1px solid"
              borderColor={borderColor}
              _hover={{ bg: hoverBg }}
              onClick={() => handleSelect(value as SelectableChartType)}
            >
              <HStack justify="space-between" align="center" w="100%">
                <HStack spacing={2}>
                  <Box color={styles.textColor}>{icon}</Box>
                  <Text fontSize="sm" fontWeight="600" color={styles.textColor}>
                    {label}
                  </Text>
                </HStack>

                {/* Radio button */}
                <Box
                  w="16px"
                  h="16px"
                  mr={1.5}
                  borderRadius="full"
                  border="2px solid"
                  borderColor={borderColor}
                  bg={radioBg}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {isSelected && (
                    <Box w="8px" h="8px" bg="white" borderRadius="full" />
                  )}
                </Box>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};
